// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useState } from 'react';
import { ChevronDown, ChevronUp, Edit2, Trash2 } from 'lucide-react';
import { formatINR } from '../../utils/currency';
import { formatDate } from '../../utils/dates';

export default function TransactionRow({
  transaction,
  detailed = false,
  onEdit,
  onDelete,
  isEditMode = false,
  isSelected = false,
  onToggleSelect = null,
  siblingAccountName = null,
}) {
  const [expanded, setExpanded] = useState(false);

  const {
    id,
    date,
    description,
    amount,
    transaction_type,
    transfer_leg,
    category_detail,
    subcategory_detail,
    merchant_name,
    notes,
  } = transaction;

  const typeColor = {
    INCOME: 'text-green-600 bg-green-50',
    EXPENSE: 'text-red-600 bg-red-50',
    SAVING: 'text-blue-600 bg-blue-50',
    TRANSFER: 'text-gray-600 bg-gray-50',
  };

  const typeLabel = {
    INCOME: '+',
    EXPENSE: '−',
    SAVING: '→',
  };

  // A transfer (#31) is two linked rows, one per account — '↔' on both told
  // you nothing about which way money moved on *this* row. The OUT leg
  // (money leaving this account) reads as '−', the IN leg as '+', same as
  // an expense/income would, but the badge/color stay neutral gray so a
  // transfer is never mistaken for real income or spending in a scan of
  // the list.
  const sign = transaction_type === 'TRANSFER'
    ? (transfer_leg === 'IN' ? '+' : '−')
    : (typeLabel[transaction_type] ?? '');
  const typeBadge = transaction_type === 'TRANSFER' && transfer_leg
    ? `TRANSFER ${transfer_leg}`
    : transaction_type;

  // The other leg's account name, when it's loaded in the same list (#31)
  // — "to Cash" / "from Cash" tells you where the money actually went
  // without having to go find the sibling row yourself.
  const transferAnnotation = transaction_type === 'TRANSFER' && siblingAccountName
    ? (transfer_leg === 'IN' ? `from ${siblingAccountName}` : `to ${siblingAccountName}`)
    : null;

  const categoryName = category_detail?.name || 'Uncategorized';
  const subcategoryName = subcategory_detail?.name;
  const categoryIcon = category_detail?.icon || '📁';

  return (
    <>
      {/* Mobile View - Compact */}
      <div className="md:hidden border-b border-gray-100 hover:bg-gray-50 transition">
        <div className="flex items-center gap-2 px-3 py-3">
          {isEditMode && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSelect?.(id)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600"
            />
          )}
          <span className="text-lg">{categoryIcon}</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {description || merchant_name || 'Transaction'}
            </p>
            <p className="text-xs text-gray-500">
              {categoryName}
              {subcategoryName && ` > ${subcategoryName}`}
              {transferAnnotation && ` · ${transferAnnotation}`}
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className={`text-sm font-semibold ${typeColor[transaction_type]}`}>
              {sign}{formatINR(amount)}
            </p>
            <p className="text-xs text-gray-500">{formatDate(date)}</p>
          </div>
          <div className="flex gap-1 shrink-0">
            {onEdit && (
              <button
                onClick={() => onEdit(transaction)}
                className="p-1 hover:bg-indigo-100 rounded text-indigo-600"
                title="Edit transaction"
              >
                <Edit2 size={14} />
              </button>
            )}
            {onDelete && (
              <button
                onClick={() => onDelete(id)}
                className="p-1 hover:bg-red-100 rounded text-red-600"
                title="Delete transaction"
              >
                <Trash2 size={14} />
              </button>
            )}
            {detailed && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="p-1 hover:bg-gray-200 rounded"
              >
                {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            )}
          </div>
        </div>
        {expanded && detailed && (
          <div className="px-3 pb-3 text-xs text-gray-600 bg-gray-50">
            {notes && <p className="mb-1"><span className="font-medium">Notes:</span> {notes}</p>}
            <p><span className="font-medium">Date:</span> {formatDate(date)}</p>
            <p><span className="font-medium">Type:</span> {typeBadge}</p>
          </div>
        )}
      </div>

      {/* Tablet & Desktop View - Detailed */}
      <div className="hidden md:flex items-center gap-4 px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition">
        {/* Checkbox (Edit Mode) */}
        {isEditMode && (
          <div className="w-8 shrink-0">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSelect?.(id)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600"
            />
          </div>
        )}

        {/* Date */}
        <span className="text-xs text-gray-500 w-20 shrink-0">
          {formatDate(date)}
        </span>

        {/* Category Icon & Name */}
        <div className="flex items-center gap-2 w-32 shrink-0">
          <span className="text-lg">{categoryIcon}</span>
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-700 truncate">
              {categoryName}
            </p>
            {subcategoryName && (
              <p className="text-xs text-gray-500 truncate">{subcategoryName}</p>
            )}
          </div>
        </div>

        {/* Description */}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-900 truncate">
            {description || merchant_name || 'Transaction'}
          </p>
          {transferAnnotation && (
            <p className="text-xs text-gray-500 truncate">{transferAnnotation}</p>
          )}
          {notes && detailed && (
            <p className="text-xs text-gray-500 truncate">{notes}</p>
          )}
        </div>

        {/* Type Badge */}
        <span className={`text-xs font-medium px-2 py-1 rounded shrink-0 ${typeColor[transaction_type]}`}>
          {typeBadge}
        </span>

        {/* Amount */}
        <span className={`text-sm font-semibold shrink-0 w-24 text-right ${typeColor[transaction_type]}`}>
          {sign}{formatINR(amount)}
        </span>

        {/* Actions */}
        {(onEdit || onDelete) && (
          <div className="flex gap-1 shrink-0">
            {onEdit && (
              <button
                onClick={() => onEdit(transaction)}
                className="p-1.5 hover:bg-indigo-100 rounded text-indigo-600"
                title="Edit transaction"
              >
                <Edit2 size={14} />
              </button>
            )}
            {onDelete && (
              <button
                onClick={() => onDelete(id)}
                className="p-1.5 hover:bg-red-100 rounded text-red-600"
                title="Delete transaction"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}
